<template>
    <div class="limiter-content pa-3 pa-lg-0">
        <div class="padding-block"></div>

        <!-- Cabecalho no padrao das telas da area do credor/procurador -->
        <header class="cr-header">
            <span class="cr-eyebrow">
                <v-icon small color="primary">{{ isProcurator ? "mdi-briefcase-account-outline" : "mdi-account-circle-outline" }}</v-icon>
                {{ isProcurator ? "ÁREA DO PROCURADOR" : "ÁREA DO CREDOR" }}
            </span>
            <h1 class="cr-title">Resumo <span>de casos</span></h1>
            <p class="cr-subtitle">Casos em que {{ isProcurator ? "o credor representado tem" : "você tem" }} crédito, com o valor e as votações abertas.</p>
        </header>

        <template v-if="user">
            <div class="cr-field">
                <procurator-creditor-selector v-model="selectedCreditorId" @loaded="onRepresentedLoaded"></procurator-creditor-selector>
            </div>

            <!-- Procurador: visao de todos os credores representados de uma vez -->
            <section v-if="representedCreditors.length > 1" class="cr-card">
                <v-switch v-model="allCreditorsMode" dense hide-details inset color="darkSecondary" class="mt-0"
                    :label="`Ver todos os credores representados de uma vez (${representedCreditors.length})`"
                    @change="onAllCreditorsModeChange"></v-switch>
                <div v-if="allCreditorsMode" class="mt-4">
                    <v-progress-linear v-if="allCreditorsLoading" indeterminate color="darkSecondary" class="mb-3"></v-progress-linear>
                    <template v-if="allCreditorsRows.length">
                        <article v-for="row in allCreditorsRows" :key="`${row.creditor_user_id}-${row.gmv_id}`" class="cr-vote-row">
                            <div class="cr-vote-row__info">
                                <div class="cr-vote-row__name">{{ row.creditor_name }}</div>
                                <div class="cr-muted">
                                    {{ row.gm_name }} · {{ row.gmv_name }}
                                    ({{ row.gmv_status | enum(dataset.agcVotingStatus, "id", "name") }})
                                </div>
                            </div>
                            <span class="cr-pill" :class="row.gmv_presence ? 'cr-pill--ok' : 'cr-pill--muted'">
                                <span class="cr-pill__dot"></span>{{ row.gmv_presence ? "Presença concedida" : "Presença não concedida" }}
                            </span>
                            <span class="cr-vote-row__vote">
                                <template v-if="row.total_credits > 1">{{ row.voted_credits }} de {{ row.total_credits }} classes</template>
                                <template v-else-if="row.gmv_vote">{{ row.gmv_vote | enum(dataset.generalMeetingVoteStatus, "id", "name") }}</template>
                                <span v-else class="cr-muted">Sem voto</span>
                            </span>
                            <v-btn small depressed color="primary" class="cr-btn cr-btn--small"
                                :disabled="row.gm_status !== 5 || row.gmv_status !== 2" @click="openVotingPageFor(row)">Votar</v-btn>
                        </article>
                    </template>
                    <p v-else-if="!allCreditorsLoading" class="cr-muted mb-0">
                        {{ dataset.selectedClient ? "Nenhuma votação aberta para os credores representados neste caso." : "Abra um caso abaixo para ver as votações dos credores representados." }}
                    </p>
                </div>
            </section>

            <!-- Solicitacoes de documentos da AJ ainda abertas para este credor -->
            <div v-if="pendingDocumentRequests.length" class="cr-notice">
                <v-icon color="#a16207" class="cr-notice__icon">mdi-file-alert-outline</v-icon>
                <div class="cr-notice__text">
                    <strong>A Administração Judicial solicitou documentos.</strong>
                    {{ pendingDocumentRequests.length }} {{ pendingDocumentRequests.length === 1 ? "solicitação aguardando" : "solicitações aguardando" }} envio,
                    prazo mais próximo em <strong>{{ nearestDocumentDeadline | defaultDate('DD/MM/YYYY') }}</strong>.
                </div>
                <v-btn depressed color="primary" class="cr-btn" @click="goToDocumentRequests">Ver documentos solicitados</v-btn>
            </div>

            <!-- Casos -->
            <div class="cr-list-head">
                <h2>Casos</h2>
                <span v-if="dataset.clients.length">{{ dataset.clients.length }} {{ dataset.clients.length === 1 ? "caso" : "casos" }}</span>
            </div>

            <template v-if="dataset.clients.length">
                <article v-for="item in dataset.clients" :key="item.id" class="cr-row" tabindex="0"
                    @click="selectClient(item)" @keyup.enter="selectClient(item)">
                    <div class="cr-row__icon"><v-icon color="darkSecondary">mdi-briefcase-outline</v-icon></div>
                    <div class="cr-row__info">
                        <div class="cr-row__name">{{ item.client.name }}</div>
                        <div class="cr-muted">Associado em {{ (item.qgc_created_at || item.created_at) | defaultDate('DD/MM/YYYY') }}</div>
                    </div>
                    <div class="cr-row__amount">
                        <span>Valor do crédito</span>
                        <strong>R$ {{ item.amount | currency }}</strong>
                    </div>
                    <div class="cr-row__actions">
                        <v-btn v-if="isProcurator" text rounded small color="darkSecondary" class="cr-action"
                            @click.stop="$router.push(`/procurador/casos/${item.client_id}/assembleia`)">
                            <v-icon left small>mdi-account-group-outline</v-icon> AGC do caso
                        </v-btn>
                        <v-btn text rounded small color="darkSecondary" class="cr-action" @click.stop="selectClient(item)">
                            Ver resumo <v-icon right small>mdi-arrow-right</v-icon>
                        </v-btn>
                    </div>
                </article>
            </template>
            <div v-else class="cr-empty">
                <div class="cr-empty__icon"><v-icon size="34" color="darkSecondary">mdi-briefcase-search-outline</v-icon></div>
                <div class="cr-empty__title">
                    {{ isProcurator && !selectedCreditorId ? "Selecione o credor representado" : "Nenhum caso associado" }}
                </div>
                <div class="cr-empty__text">
                    {{ isProcurator && !selectedCreditorId ? "Os casos aparecem depois que você escolhe o credor acima." : "Quando houver crédito em um caso acompanhado pela Brizola e Japur, ele aparece aqui." }}
                </div>
            </div>
        </template>

        <template v-else>
            <login-required-notice />
            <div v-if="dataset.contentUserAnonymous" class="cr-anonymous" v-html="dataset.contentUserAnonymous"></div>
        </template>

        <!-- Resumo do caso -->
        <v-dialog v-model="dialog" :width="!timelineDrawer ? '800px' : '1000px'" scrollable>
            <v-card v-if="dataset.selectedClient" class="cr-dialog">
                <div class="cr-dialog__head">
                    <div>
                        <span class="cr-eyebrow">RESUMO DO CASO</span>
                        <h2 class="cr-dialog__title">{{ dataset.selectedClient.client.name }}</h2>
                    </div>
                    <v-btn icon @click="closeDialog()" aria-label="Fechar"><v-icon>mdi-close</v-icon></v-btn>
                </div>

                <v-card-text class="cr-dialog__body">
                    <v-row no-gutters>
                        <v-col>
                            <div class="cr-amount-box">
                                <span>Valor para recebimento</span>
                                <strong>R$ {{ dataset.selectedClient.amount | currency }}</strong>
                            </div>

                            <div class="cr-list-head mt-6">
                                <h2>Assembleias e votações</h2>
                                <span>
                                    {{ !dataset.generalMeetings.length
                                        ? "Nenhuma votação aberta no momento"
                                        : `Credenciado em ${dataset.generalMeetings.length} ${dataset.generalMeetings.length === 1 ? "votação aberta" : "votações abertas"}` }}
                                </span>
                            </div>

                            <v-expansion-panels flat class="cr-panels">
                                <v-expansion-panel v-for="(item, index) in dataset.generalMeetings" :key="index">
                                    <v-expansion-panel-header>
                                        <div class="cr-panel-header">
                                            <span class="cr-panel-title">{{ item.gm_name }}</span>
                                            <span class="cr-pill" :class="`cr-pill--${meetingTone(item.gm_status)}`">
                                                <span class="cr-pill__dot"></span>{{ item.gm_status | enum(dataset.generalMeetingStatus, "id", "name") }}
                                            </span>
                                        </div>
                                    </v-expansion-panel-header>
                                    <v-expansion-panel-content>
                                        <p class="cr-muted mb-4">
                                            Credenciamento realizado em <strong>{{ item.agm_created_at | defaultDate('DD/MM/YYYY') }}</strong>
                                        </p>

                                        <div class="cr-voting">
                                            <div class="cr-voting__head">
                                                <strong>{{ item.gmv_name }}</strong>
                                                <span class="cr-pill" :class="`cr-pill--${votingTone(item.gmv_status)}`">
                                                    <span class="cr-pill__dot"></span>Votação {{ item.gmv_status | enum(dataset.agcVotingStatus, "id", "name") }}
                                                </span>
                                                <span class="cr-pill" :class="item.gmv_presence && !item.continuation_blocked ? 'cr-pill--ok' : 'cr-pill--muted'">
                                                    <v-icon x-small>{{ item.gmv_presence ? 'mdi-account-check' : 'mdi-account-clock-outline' }}</v-icon>
                                                    {{ item.continuation_blocked ? 'Sem direito de participar' : (item.gmv_presence ? 'Presença concedida' : 'Presença não concedida') }}
                                                </span>
                                            </div>

                                            <p v-if="!item.gmv_presence" class="cr-voting__text">Sua presença ainda não foi confirmada.</p>

                                            <template v-else-if="item.total_credits > 1">
                                                <p class="cr-voting__text">Você tem crédito em {{ item.total_credits }} classes. O voto é registrado por classe:</p>
                                                <div v-for="credit in item.credits" :key="credit.user_creditor_id" class="cr-class-vote">
                                                    <span>{{ credit.class_name }}</span>
                                                    <span v-if="credit.vote" class="cr-vote" :class="`cr-vote--${voteDetails(credit.vote).tone}`">
                                                        <v-icon x-small>{{ voteDetails(credit.vote).icon }}</v-icon>
                                                        {{ credit.vote | enum(dataset.generalMeetingVoteStatus, "id", "name") }}
                                                    </span>
                                                    <span v-else class="cr-vote cr-vote--none">Não votou</span>
                                                </div>
                                                <div class="cr-voting__footer">
                                                    <span>
                                                        {{ item.voted_credits === item.total_credits
                                                            ? "Você já votou em todas as classes."
                                                            : `Você votou em ${item.voted_credits} de ${item.total_credits} classes.` }}
                                                    </span>
                                                    <v-btn depressed color="primary" class="cr-btn"
                                                        :disabled="item.gm_status !== 5 || item.gmv_status !== 2" @click="openVotingPage(item)">
                                                        {{ item.voted_credits === item.total_credits ? 'Alterar voto' : 'Votar' }}
                                                        <v-icon right small>mdi-arrow-right</v-icon>
                                                    </v-btn>
                                                </div>
                                            </template>

                                            <div v-else class="cr-voting__footer">
                                                <span v-if="item.gmv_vote">
                                                    Você já votou {{ votingObjectText(item) }}:
                                                    <span class="cr-vote" :class="`cr-vote--${voteDetails(item.gmv_vote).tone}`">
                                                        <v-icon x-small>{{ voteDetails(item.gmv_vote).icon }}</v-icon>
                                                        {{ item.gmv_vote | enum(dataset.generalMeetingVoteStatus, "id", "name") }}
                                                    </span>
                                                </span>
                                                <span v-else>Você ainda não votou {{ votingObjectText(item) }}.</span>
                                                <v-btn depressed color="primary" class="cr-btn"
                                                    :disabled="item.gm_status !== 5 || item.gmv_status !== 2" @click="openVotingPage(item)">
                                                    {{ item.gmv_vote ? 'Alterar voto' : 'Votar' }}
                                                    <v-icon right small>mdi-arrow-right</v-icon>
                                                </v-btn>
                                            </div>
                                        </div>
                                    </v-expansion-panel-content>
                                </v-expansion-panel>
                            </v-expansion-panels>
                        </v-col>
                        <v-col sm="4" v-if="timelineDrawer">
                            <v-timeline class="ml-10" dense clipped>
                                <v-timeline-item
                                    v-for="(event, index) in [{ date: '12/01/2023', name: 'Conta registrada' }, { date: '13/01/2023', name: 'Enviou uma habilitação' }, { date: '20/01/2023', name: 'Habilitação aprovada' }]"
                                    :key="index" color="primary" small dense>
                                    <div class="future-meeting-card">
                                        <div>{{ event.date }}</div>
                                        <div>{{ event.name }}</div>
                                        <div>{{ event.description }}</div>
                                    </div>
                                </v-timeline-item>
                            </v-timeline>
                        </v-col>
                    </v-row>
                </v-card-text>
            </v-card>
        </v-dialog>
    </div>
</template>


<script>

import axios from "axios"

import generalMeetingStatus from "@/common/data/general-meeting-status.data"
import generalMeetingVoteStatus from "@/common/data/general-meeting-vote-status.data"
import agcVotingStatus from "@/common/data/agc-voting-status.data"
import ProcuratorCreditorSelector from '@/components/ProcuratorCreditorSelector.vue'

export default {
    name: 'CasesResume',
    components: {
        ProcuratorCreditorSelector,
    },
    data() {
        return {
            user: null,
            selectedCreditorId: null,
            intervalId: null,
            // Modo "todos os credores representados" (procurador): usados direto no template.
            representedCreditors: [],
            allCreditorsMode: false,
            allCreditorsLoading: false,
            allCreditorsRows: [],
            dataset: {
                generalMeetingStatus: generalMeetingStatus,
                generalMeetingVoteStatus: generalMeetingVoteStatus,
                agcVotingStatus: agcVotingStatus,
                clients: [],
                generalMeetings: [],
                selectedClient: null,
                contentUserAnonymous: '',
                generalMeetingVoting: null,
                documentRequests: [],
            },
            dialog: false,
            timelineDrawer: false,
        };
    },

    watch: {
        selectedCreditorId() {
            this.dataset.selectedClient = null;
            this.dataset.generalMeetings = [];
            this.loadData();
        },
        dialog(val) {
            if (!val) {
                this.stopAutoUpdate();
            }
        }
    },

    computed: {
        // Solicitacoes de documentos da AJ ainda abertas (dentro do prazo e com documento pendente).
        pendingDocumentRequests() {
            return (this.dataset.documentRequests || []).filter((request) =>
                !request.is_closed && (request.documents || []).some((doc) => !doc.uploaded)
            );
        },

        nearestDocumentDeadline() {
            const deadlines = this.pendingDocumentRequests.map((request) => request.deadline).filter(Boolean).sort();
            return deadlines.length ? deadlines[0] : null;
        },

        isProcurator() {
            return !!this.user && Number(this.user.type) === 2;
        },
    },


    created() {
        if (this.$root.user) {
            this.user = this.$root.user;
            this.loadData()
        }

        this.loadText();

    },

    destroyed() {
        this.stopAutoUpdate();
    },

    methods: {
        isOpenVoting(item) {
            return Number(item.gmv_status) === 2;
        },
        onRepresentedLoaded(items) {
            this.representedCreditors = (items || []).filter((item) => Number(item.status) === 1 && item.creditor_user_id);
        },
        onAllCreditorsModeChange(value) {
            if (value) this.loadAllCreditorsResume();
        },
        // Uma chamada por credor representado, no caso selecionado; junta tudo numa tabela.
        async loadAllCreditorsResume() {
            const client = this.dataset.selectedClient;
            if (!client || !client.client_id) {
                this.allCreditorsRows = [];
                return;
            }
            this.allCreditorsLoading = true;
            const rows = [];
            try {
                for (const creditor of this.representedCreditors) {
                    try {
                        const result = await axios.get('/api/general-meetings/resume', {
                            params: { client_id: client.client_id, creditor_user_id: creditor.creditor_user_id },
                        });
                        (result.data.content || []).filter(this.isOpenVoting).forEach((item) => rows.push({ ...item, creditor_user_id: creditor.creditor_user_id, creditor_name: creditor.creditor_name }));
                    } catch (error) {
                        rows.push({ creditor_user_id: creditor.creditor_user_id, creditor_name: creditor.creditor_name, gm_name: 'Erro ao carregar', gmv_name: '', gmv_id: 'erro', gmv_status: null, gmv_presence: false, total_credits: 0, voted_credits: 0 });
                    }
                }
            } finally {
                this.allCreditorsRows = rows;
                this.allCreditorsLoading = false;
            }
        },
        openVotingPageFor(row) {
            if (!row || !row.gmv_id) return;
            this.$router.push({ path: `/assembleia-credores/votacao/${row.gmv_id}`, query: { creditor_user_id: row.creditor_user_id } });
        },
        // Objeto da votacao nas frases, em vez do texto fixo "o plano".
        votingObjectText(item) {
            const subject = item && item.gmv_subject ? String(item.gmv_subject) : 'plan';
            const name = item && item.gmv_name ? String(item.gmv_name).trim() : '';
            if (subject === 'suspension') return 'a suspensão da assembleia';
            if (subject === 'other') return name ? `a deliberação "${name}"` : 'a deliberação';
            return 'o plano de recuperação';
        },

        selectClient(item) {
            this.dataset.selectedClient = item;
            this.dialog = true;

            if (this.allCreditorsMode) this.loadAllCreditorsResume();

            this.loadGeneralMeetingByClientAndUser();

            this.startAutoUpdate();
        },
        closeDrawer() {
            this.timelineDrawer = false;
        },

        // Tom da pilula do status da assembleia (mesmas faixas das cores antigas).
        meetingTone(status) {
            return { 0: "info", 1: "info", 2: "danger", 3: "warn", 4: "muted", 5: "ok" }[Number(status)] || "muted";
        },

        // Tom da pilula do status da votacao: aberta em verde.
        votingTone(status) {
            return { 1: "muted", 2: "ok", 3: "warn", 4: "danger" }[Number(status)] || "muted";
        },

        async loadData() {
            if (!this.$root.user) return;
            if (this.isProcurator && !this.selectedCreditorId) {
                this.dataset.clients = [];
                return;
            }

            await this.loadAssociation();
            await this.loadGeneralMeetingVoting();
            await this.loadDocumentRequests();
        },

        async loadAssociation() {
            try {
                const result = await axios.get(`/api/users/association`, {
                    params: {
                        creditor_user_id: this.selectedCreditorId || undefined,
                    }
                })

                this.dataset.clients = result.data.content.data;

            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), {
                    position: 'top-right',
                    timeout: 2000
                })
            }
        },

        async loadGeneralMeetingByClientAndUser() {
            try {
                const result = await axios.get(`/api/general-meetings/resume`, {
                    params: {
                        client_id: this.dataset.selectedClient.client_id,
                        creditor_user_id: this.selectedCreditorId || undefined,
                    }
                })

                this.dataset.generalMeetings = (result.data.content || []).filter(this.isOpenVoting);


            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), {
                    position: 'top-right',
                    timeout: 2000
                })
            }
        },

        startAutoUpdate() {
            if (!this.intervalId) {
                this.intervalId = setInterval(() => {
                    this.loadGeneralMeetingByClientAndUser();
                }, 15000); // 15 segundos
            }
        },

        stopAutoUpdate() {
            if (this.intervalId) {
                clearInterval(this.intervalId);
                this.intervalId = null;
            }
        },

        closeDialog() {

            this.stopAutoUpdate();

            this.dialog = false;
        },


        async loadDocumentRequests() {
            try {
                const result = await axios.get('/api/document-requests', {
                    params: { creditor_user_id: this.selectedCreditorId || undefined },
                });
                this.dataset.documentRequests = Array.isArray(result.data.content) ? result.data.content : [];
            } catch (error) {
                this.dataset.documentRequests = [];
            }
        },

        goToDocumentRequests() {
            const route = { path: '/documentos-solicitados' };
            if (this.selectedCreditorId) route.query = { creditor_user_id: this.selectedCreditorId };
            this.$router.push(route);
        },

        async loadGeneralMeetingVoting() {

            try {
                const result = await axios.get('/api/general-meeting-votings');

                this.dataset.generalMeetingVoting = result.data.content.data[0];

            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 5000 });
            } finally {
                this.loading = false;
            }

        },

        voteDetails(vote) {
            switch (vote) {
                case 1:
                    return { icon: 'mdi-thumb-up', tone: 'ok' };
                case 2:
                    return { icon: 'mdi-thumb-down', tone: 'danger' };
                case 3:
                    return { icon: 'mdi-cancel', tone: 'muted' };
                default:
                    return { icon: 'mdi-progress-alert', tone: 'warn' };
            }
        },


        openVotingPage(item) {
            this.stopAutoUpdate();

            this.$router.push(`/assembleia-credores/votacao/${item.gmv_id}`);
        },

        async loadText() {
            try {
                const result = await axios.get(`/api/dynamic-infos/keys`, {
                    params: { keys: 'resume-description-user-anonymous' }
                });

                this.dataset.contentUserAnonymous = result.data.content && result.data.content.length > 0 ? result.data.content[0].value : '';

            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), {
                    position: 'top-right',
                    timeout: 2000
                });
            }
        }
    },
};
</script>

<style scoped>
/* Mesmo padrao visual de "Meus credores" e "Minha conta". */

.cr-header {
    padding: 32px 0 28px;
}

.cr-eyebrow {
    display: flex;
    gap: 8px;
    align-items: center;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.12em;
    color: #766386;
}

.cr-title {
    margin: 14px 0 10px;
    font-size: clamp(28px, 3.4vw, 40px);
    font-weight: 700;
    color: #25113c;
    letter-spacing: -0.035em;
    line-height: 1.2;
}

.cr-title span {
    color: #806697;
}

.cr-subtitle {
    margin: 0;
    color: #73677c;
    font-size: 14px;
}

.cr-muted {
    font-size: 13px;
    color: rgba(24, 0, 50, 0.55);
}

/* Seletor do credor representado com o mesmo contorno lilas */
.cr-field {
    max-width: 520px;
}

.cr-field ::v-deep .v-text-field--outlined fieldset {
    border-color: rgba(126, 85, 171, 0.45);
    border-radius: 10px;
}

.cr-field ::v-deep .v-text-field--outlined:hover fieldset {
    border-color: #7e55ab;
}

.cr-field ::v-deep .v-input__slot {
    border-radius: 10px !important;
}

.cr-field ::v-deep .v-alert {
    border-radius: 12px;
}

/* Cartao e aviso */
.cr-card {
    padding: 18px 22px;
    border-radius: 16px;
    background: #faf9fd;
    border: 1px solid rgba(24, 0, 50, 0.06);
    margin-bottom: 20px;
}

.cr-notice {
    display: flex;
    align-items: center;
    gap: 12px 14px;
    flex-wrap: wrap;
    padding: 14px 18px;
    border-radius: 14px;
    background: #fff7e6;
    color: #2a1a40;
    font-size: 14px;
    line-height: 1.55;
    margin-bottom: 24px;
}

.cr-notice__text {
    flex: 1 1 320px;
}

/* Botoes */
.cr-btn {
    height: 40px !important;
    padding: 0 18px !important;
    border-radius: 10px;
    text-transform: none;
    letter-spacing: 0;
    font-size: 14px;
    font-weight: 600;
}

.cr-btn--small {
    height: 32px !important;
    padding: 0 14px !important;
    font-size: 13px;
}

.cr-action {
    text-transform: none;
    letter-spacing: normal;
    font-weight: 600;
}

/* Lista de casos */
.cr-list-head {
    display: flex;
    align-items: baseline;
    gap: 10px;
    flex-wrap: wrap;
    margin: 8px 0 12px;
}

.cr-list-head h2 {
    margin: 0;
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #7e55ab;
}

.cr-list-head span {
    font-size: 12px;
    color: rgba(24, 0, 50, 0.5);
}

.cr-row {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr) auto auto;
    align-items: center;
    gap: 18px;
    padding: 16px 18px;
    border-radius: 16px;
    background: #fff;
    border: 1px solid rgba(24, 0, 50, 0.06);
    margin-bottom: 8px;
    cursor: pointer;
    outline: none;
    transition: box-shadow 0.18s ease, border-color 0.18s ease;
}

.cr-row:hover,
.cr-row:focus-visible {
    border-color: rgba(120, 119, 229, 0.35);
    box-shadow: 0 6px 20px rgba(24, 0, 50, 0.07);
}

.cr-row__icon {
    width: 44px;
    height: 44px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(120, 119, 229, 0.12);
}

.cr-row__name {
    font-size: 15px;
    font-weight: 600;
    color: #180032;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.cr-row__amount {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    line-height: 1.3;
}

.cr-row__amount span {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #8b769d;
}

.cr-row__amount strong {
    font-size: 17px;
    color: #180032;
    white-space: nowrap;
}

.cr-row__actions {
    display: flex;
    align-items: center;
    gap: 2px;
}

/* Votacoes de todos os credores representados */
.cr-vote-row {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto auto auto;
    align-items: center;
    gap: 14px;
    padding: 12px 0;
    border-top: 1px solid rgba(24, 0, 50, 0.06);
}

.cr-vote-row__name {
    font-size: 14px;
    font-weight: 600;
    color: #180032;
    text-transform: capitalize;
}

.cr-vote-row__vote {
    font-size: 13px;
    color: #25113c;
    white-space: nowrap;
}

/* Pilulas de status */
.cr-pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 12px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 600;
    white-space: nowrap;
}

.cr-pill .v-icon {
    color: inherit;
}

.cr-pill__dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: currentColor;
}

.cr-pill--ok {
    background: #e7f6ee;
    color: #1e7b4f;
}

.cr-pill--info {
    background: rgba(120, 119, 229, 0.14);
    color: #4b3a8f;
}

.cr-pill--warn {
    background: #fff4dc;
    color: #a16207;
}

.cr-pill--danger {
    background: #fdecea;
    color: #b42318;
}

.cr-pill--muted {
    background: #f1f0f4;
    color: #6b6475;
}

/* Estado vazio e visitante */
.cr-empty {
    text-align: center;
    padding: 48px 16px;
}

.cr-empty__icon {
    width: 72px;
    height: 72px;
    border-radius: 50%;
    margin: 0 auto 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(120, 119, 229, 0.12);
}

.cr-empty__title {
    font-size: 17px;
    font-weight: 600;
    color: #180032;
}

.cr-empty__text {
    margin-top: 6px;
    color: rgba(24, 0, 50, 0.6);
    font-size: 14px;
}

.cr-anonymous {
    padding: 20px 22px;
    border-radius: 16px;
    background: rgba(120, 119, 229, 0.09);
    color: #2a1a40;
    font-weight: 600;
}

/* Dialogo do resumo do caso */
.cr-dialog {
    border-radius: 18px !important;
}

.cr-dialog__head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    padding: 22px 22px 8px;
}

.cr-dialog__title {
    margin: 8px 0 0;
    font-size: 22px;
    font-weight: 700;
    color: #25113c;
    letter-spacing: -0.02em;
    line-height: 1.25;
}

.cr-dialog__body {
    padding: 8px 22px 24px !important;
}

.cr-amount-box {
    display: flex;
    flex-direction: column;
    padding: 14px 18px;
    border-radius: 14px;
    background: rgba(120, 119, 229, 0.09);
}

.cr-amount-box span {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #7e55ab;
}

.cr-amount-box strong {
    font-size: 22px;
    color: #180032;
}

.cr-panels ::v-deep .v-expansion-panel {
    border: 1px solid rgba(24, 0, 50, 0.08);
    border-radius: 14px !important;
    margin-bottom: 8px;
}

.cr-panels ::v-deep .v-expansion-panel::before {
    box-shadow: none;
}

.cr-panels ::v-deep .v-expansion-panel::after {
    border-top: 0 !important;
}

.cr-panel-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    width: 100%;
    min-width: 0;
    padding-right: 8px;
}

.cr-panel-title {
    flex: 1 1 auto;
    min-width: 0;
    font-weight: 600;
    color: #180032;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.cr-voting {
    padding: 14px 16px;
    border-radius: 12px;
    background: #faf9fd;
}

.cr-voting__head {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px 10px;
    color: #180032;
}

.cr-voting__text {
    margin: 12px 0 0;
    font-size: 14px;
    color: #2a1a40;
}

.cr-voting__footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 10px 16px;
    margin-top: 14px;
    font-size: 14px;
    color: #2a1a40;
}

.cr-class-vote {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 8px 0;
    border-bottom: 1px solid rgba(24, 0, 50, 0.06);
    font-size: 14px;
    font-weight: 600;
    color: #180032;
}

.cr-vote {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 3px 10px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 600;
}

.cr-vote .v-icon {
    color: inherit;
}

.cr-vote--ok {
    background: #e7f6ee;
    color: #1e7b4f;
}

.cr-vote--danger {
    background: #fdecea;
    color: #b42318;
}

.cr-vote--muted,
.cr-vote--none {
    background: #f1f0f4;
    color: #6b6475;
}

.cr-vote--warn {
    background: #fff4dc;
    color: #a16207;
}

.future-meeting-card {
    background-color: #f5f5f5;
    padding: 10px;
    border-radius: 8px;
    font-size: 0.9em;
}

/* Celular */
@media (max-width: 760px) {
    .cr-row {
        grid-template-columns: auto minmax(0, 1fr);
        gap: 10px 14px;
    }

    .cr-row__amount {
        grid-column: 2;
        align-items: flex-start;
    }

    .cr-row__actions {
        grid-column: 1 / -1;
        flex-wrap: wrap;
        border-top: 1px solid rgba(24, 0, 50, 0.06);
        padding-top: 6px;
    }

    .cr-row__name {
        white-space: normal;
    }

    .cr-vote-row {
        grid-template-columns: minmax(0, 1fr) auto;
    }

    .cr-panel-header {
        flex-wrap: wrap;
    }

    .cr-panel-title {
        white-space: normal;
        flex-basis: 100%;
    }
}
</style>
